import { useRef, useState, useEffect, useCallback } from "react";
import { ProductProvider } from "@made-people/grease-product";
import { useTranslate } from "@made-people/grease-translations";
import {
  useSearchResultItems,
  type SearchResultItemProduct,
} from "@made-people/grease-search";
import { MMUpsellProductCard } from "./MMUpsellProductCard";

interface MMUpsellProductListProps {
  heading: string;
  context?: "minicart" | "checkout";
  className?: string;
}

const MMUpsellProductListSkeleton = ({
  context,
  className,
}: {
  context?: string;
  className?: string;
}) => (
  <div
    className={`flex animate-pulse flex-col gap-4 bg-[#F3F2EF] px-4 py-6 lg:mx-0 w-full lg:rounded-md ${className ?? ""}`}
    data-context={context}
    aria-hidden="true"
  >
    <div className="flex flex-col items-center gap-2">
      <div className="h-5 w-48 rounded-full bg-neutral-200" />
    </div>
    <div className="flex gap-4 overflow-hidden px-6">
      {Array.from({ length: 2 }).map((_, index) => (
        <div
          key={index}
          className="flex h-36 w-[310px] shrink-0 gap-4 rounded bg-white p-4"
        >
          <div className="h-28 w-[84px] shrink-0 rounded bg-neutral-200" />
          <div className="flex flex-1 flex-col justify-between">
            <div className="flex flex-col gap-1.5">
              <div className="h-4 w-3/4 rounded-full bg-neutral-200" />
              <div className="h-4 w-1/3 rounded-full bg-neutral-200" />
            </div>
            <div className="h-10 w-full rounded bg-neutral-200" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const MMUpsellProductList = ({
  heading,
  context = "checkout",
  className,
}: MMUpsellProductListProps) => {
  const items = useSearchResultItems();
  const translate = useTranslate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const products =
    items
      ?.filter(
        (item): item is SearchResultItemProduct => item.type === "product",
      )
      .map((item) => item.product) ?? [];

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft > 0 && container.scrollLeft >= maxScrollLeft - 2) {
      setActiveIndex(products.length - 1);
      return;
    }
    const cardWidth = container.firstElementChild
      ? (container.firstElementChild as HTMLElement).offsetWidth
      : 1;
    const gap = 16;
    const index = Math.round(container.scrollLeft / (cardWidth + gap));
    setActiveIndex(Math.min(index, products.length - 1));
  }, [products.length]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const getCardStep = () => {
    const container = scrollRef.current;
    if (!container) return 326;
    const card = container.firstElementChild as HTMLElement | null;
    return card ? card.offsetWidth + 16 : 326;
  };

  const scrollToIndex = (index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({
      left: index * getCardStep(),
      behavior: "smooth",
    });
  };

  const scrollByOne = (direction: -1 | 1) => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollBy({
      left: direction * getCardStep(),
      behavior: "smooth",
    });
  };

  const canScrollPrev = activeIndex > 0;
  const canScrollNext = activeIndex < products.length - 1;

  if (!items)
    return (
      <MMUpsellProductListSkeleton context={context} className={className} />
    );

  if (!products.length) {
    return null;
  }

  return (
    <div
      className={`flex flex-col lg:w-full data-[context=minicart]/upsell:mt-4 data-[context=minicart]:rounded-lg data-[context=minicart]/upsell:mb-2 data-[context=minicart]/upsell:lg:w-full gap-4 bg-[#F3F2EF] py-6 lg:mx-0 w-full lg:rounded-md group/upsell ${className ?? ""} data-[context=checkout]/upsell:w-[calc(100%+32px)] data-[context=checkout]/upsell:lg:w-[calc(100%+48px)]
data-[context=checkout]/upsell:lg:mt-4  data-[context=checkout]/upsell:-ml-4 data-[context=checkout]/upsell:lg:-ml-6 data-[context=checkout]/upsell:lg:-mb-6 data-[context=checkout]/upsell:lg:rounded-none`}
      data-context={context}
    >
      <div className="flex flex-col gap-2 text-center">
        <h3 className="text-heading-sm font-semibold group-data-[context=checkout]/upsell:lg:text-left group-data-[context=checkout]/upsell:lg:pl-6 mb-1 lg:mb-2">
          {translate(heading)}
        </h3>
      </div>

      <div className="relative">
        <div
          ref={scrollRef}
          className="flex snap-x snap-mandatory snap-center gap-4 overflow-x-auto px-6 scrollbar-hide"
        >
          {products.map((product, index) => (
            <div
              key={product.base.productId}
              className="w-[80%] lg:min-w-72 shrink-0 snap-center lg:w-[calc(50%-0.5rem)] group-data-[context=checkout]/upsell:lg:w-[80%]"
            >
              <ProductProvider
                product={product}
                context={{
                  type: "plp",
                  listId: "upsell",
                  listName: "Upsell",
                  position: index,
                }}
              >
                <MMUpsellProductCard context={context} />
              </ProductProvider>
            </div>
          ))}
        </div>

        {products.length > 1 && (
          <>
            <button
              onClick={() => scrollByOne(-1)}
              className={`absolute left-2 [&>svg]:w-6 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md size-10 lg:flex transition-all duration-200 ${
                canScrollPrev
                  ? "scale-100 opacity-100"
                  : "pointer-events-none scale-0 opacity-0"
              }`}
              aria-label="Previous"
            >
              <ChevronLeftIcon />
            </button>
            <button
              onClick={() => scrollByOne(1)}
              className={`absolute [&>svg]:w-6 right-2 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md size-10 lg:flex transition-all duration-200 ${
                canScrollNext
                  ? "scale-100 opacity-100"
                  : "pointer-events-none scale-0 opacity-0"
              }`}
              aria-label="Next"
            >
              <ChevronRightIcon />
            </button>
          </>
        )}
      </div>

      {products.length > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-2 w-2 rounded-full transition-colors ${
                index === activeIndex ? "bg-neutral-800" : "bg-neutral-400"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ChevronLeftIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 12L6 8L10 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 4L10 8L6 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
